import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, FileText, Users2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function MyCyclePage() {
  const { cycleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [participation, setParticipation] = useState(null);
  const [selfRef, setSelfRef] = useState(null);
  const [peerRequests, setPeerRequests] = useState([]);
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, mine, sr, pr, pkt] = await Promise.all([
        api.get(`/lift/cycles/${cycleId}`),
        api.get("/lift/my-participations"),
        api.get(`/lift/self-reflection/${cycleId}`),
        api.get(`/lift/peer-requests/${cycleId}/${user.id}`),
        api.get(`/lift/synthesis/${cycleId}/${user.id}`).catch(() => ({ data: null })),
      ]);
      setCycle(c.data);
      setParticipation(mine.data.find((p) => p.cycle_id === cycleId) || null);
      setSelfRef(sr.data);
      setPeerRequests(pr.data);
      setPacket(pkt?.data || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  if (loading || !cycle) return <p className="italic text-charcoal-muted">Loading…</p>;

  const packetReady = packet?.packet_released_at || participation?.packet_released_at;

  return (
    <div className="space-y-6" data-testid="lift-my-cycle-page">
      <Link to="/lift" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> LIFT home
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">{cycle.name}</h1>
        <p className="text-sm text-charcoal-muted mt-1">
          Deadline · {cycle.deadline}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ActionTile
          testId="tile-self-reflection"
          badge={selfRef?.status === "submitted" ? "Submitted" : "Not started"}
          badgeColor={selfRef?.status === "submitted" ? "green" : "terra"}
          title="Self-reflection"
          body="A short reflection on accomplishments, strengths, and growth."
          onClick={() => navigate(`/lift/self-reflection/${cycleId}`)}
        />
        <ActionTile
          testId="tile-peer-requests"
          badge={`${peerRequests.filter((r) => r.status === "submitted").length}/${peerRequests.length} responded`}
          badgeColor="green"
          title="Peer feedback requests"
          body="Ask peers who see your work each week to share their voice."
          onClick={() => navigate(`/lift/peer-requests/${cycleId}`)}
        />
        <ActionTile
          testId="tile-packet"
          badge={packetReady ? "Ready" : "Waiting"}
          badgeColor={packetReady ? "green" : "terra"}
          title="My review packet"
          body="Once your manager finalizes, review and acknowledge here."
          onClick={() => navigate(`/lift/packet/${cycleId}/${user.id}`)}
          disabled={!packetReady}
        />
      </div>
    </div>
  );
}

function ActionTile({ testId, title, body, badge, badgeColor = "green", onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="patria-card patria-card-hover p-6 text-left disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <span className={badgeColor === "terra" ? "patria-chip-terra" : "patria-chip-green"}>
        {badge}
      </span>
      <h3 className="mt-3 text-xl font-serif text-forest-900">{title}</h3>
      <p className="mt-1 text-sm text-charcoal-soft">{body}</p>
      {!disabled && (
        <p className="mt-3 inline-flex items-center text-forest-600 text-sm font-medium">
          Open <ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
        </p>
      )}
    </button>
  );
}
